import {
  BillingBillStatus,
  BillingLineStatus,
  BillingType,
  ApprovalStatus,
  SourceDocType,
  BillingBillOperationType,
  BillCategory,
  SapPackStatus,
  OutStockStatus
} from '../types';

/** 开票单状态 → 显示文本 */
export const billingBillStatusTextMap: Record<string, string> = {
  [BillingBillStatus.PENDING]: '待确认',
  [BillingBillStatus.CONFIRMING]: '确认中',
  [BillingBillStatus.CONFIRMED]: '已确认'
};

/** 开票单状态 → Tag 颜色 (ant-design) */
export const billingBillStatusColorMap: Record<string, string> = {
  [BillingBillStatus.PENDING]: 'default',
  [BillingBillStatus.CONFIRMING]: 'processing',
  [BillingBillStatus.CONFIRMED]: 'success'
};

/** 开票行状态 → 显示文本 */
export const billingLineStatusTextMap: Record<string, string> = {
  [BillingLineStatus.UNPACKED]: '未打包',
  [BillingLineStatus.PACKING]: '打包中',
  [BillingLineStatus.PACKED]: '已打包',
  [BillingLineStatus.UPLOADED]: '已上传',
  [BillingLineStatus.INVOICE_FAILED]: '开票失败',
  [BillingLineStatus.CANCELLED]: '已取消',
  [BillingLineStatus.INVOICED]: '已开票'
};

/** 开票行状态 → Tag 颜色 */
export const billingLineStatusColorMap: Record<string, string> = {
  [BillingLineStatus.UNPACKED]: 'default',
  [BillingLineStatus.PACKING]: 'processing',
  [BillingLineStatus.PACKED]: 'processing',
  [BillingLineStatus.UPLOADED]: 'cyan',
  [BillingLineStatus.INVOICE_FAILED]: 'error',
  [BillingLineStatus.CANCELLED]: 'default',
  [BillingLineStatus.INVOICED]: 'success'
};

/** 开票类型 → 显示文本 */
export const billingTypeTextMap: Record<string, string> = {
  [BillingType.VAT_SPECIAL]: '增值税专用发票',
  [BillingType.VAT_NORMAL]: '增值税普通发票',
  [BillingType.VAT_ELECTRONIC]: '电子发票',
  [BillingType.RED_INVOICE]: '红票'
};

/** 审批状态 → 显示文本 */
export const approvalStatusTextMap: Record<string, string> = {
  [ApprovalStatus.PENDING_APPROVAL]: '待审批',
  [ApprovalStatus.APPROVED]: '已通过',
  [ApprovalStatus.REJECTED]: '已驳回',
  [ApprovalStatus.DIRECT_EFFECTIVE]: '直接生效'
};

/** 审批状态 → Tag 颜色 */
export const approvalStatusColorMap: Record<string, string> = {
  [ApprovalStatus.PENDING_APPROVAL]: 'processing',
  [ApprovalStatus.APPROVED]: 'success',
  [ApprovalStatus.REJECTED]: 'error',
  [ApprovalStatus.DIRECT_EFFECTIVE]: 'success'
};

/** 开票单操作类型 → 显示文本 */
export const billingBillOperationTypeTextMap: Record<string, string> = {
  [BillingBillOperationType.CONFIRM]: '确认',
  [BillingBillOperationType.EDIT]: '编辑',
  [BillingBillOperationType.IMPORT]: '导入',
  [BillingBillOperationType.EXPORT]: '导出明细',
  [BillingBillOperationType.DOWNLOAD]: '下载发票',
  [BillingBillOperationType.SPECIFY]: '指定开票',
  [BillingBillOperationType.CANCEL_PACK]: '取消打包',
  [BillingBillOperationType.REFRESH]: '刷新状态'
};

/** 不可重复发起打包的行状态 */
export const NON_REPACKABLE_LINE_STATUSES: BillingLineStatus[] = [
  BillingLineStatus.PACKING,
  BillingLineStatus.PACKED,
  BillingLineStatus.UPLOADED,
  BillingLineStatus.INVOICED
];

/** 不允许取消打包的行状态（已上传/已开票为不可逆终态） */
export const NON_CANCELLABLE_LINE_STATUSES: BillingLineStatus[] = [
  BillingLineStatus.UPLOADED,
  BillingLineStatus.INVOICED
];

/** 可发起打包的行状态（含重新发起：开票失败行无需先取消旧包即可直接重发） */
export const PACKABLE_LINE_STATUSES: BillingLineStatus[] = [
  BillingLineStatus.UNPACKED,
  BillingLineStatus.CANCELLED,
  BillingLineStatus.INVOICE_FAILED
];

/** 可取消打包的行状态（已打包允许取消，开票失败经 SAP 校验后允许取消） */
export const CANCELABLE_LINE_STATUSES: BillingLineStatus[] = [
  BillingLineStatus.PACKED,
  BillingLineStatus.INVOICE_FAILED
];

/** 可发起指定开票的行状态（含重新发起：开票失败行可直接重发） */
export const SPECIFIABLE_LINE_STATUSES: BillingLineStatus[] = [
  BillingLineStatus.UNPACKED,
  BillingLineStatus.CANCELLED,
  BillingLineStatus.INVOICE_FAILED
];

/** 开票单类别 → 显示文本 */
export const billCategoryTextMap: Record<string, string> = {
  [BillCategory.NORMAL]: '常规开票',
  [BillCategory.INDEPENDENT]: '独立开票'
};

/** SAP 打包开票状态（90单维度，仓外同步链路写入，CRM 只读）→ 显示文本 */
export const sapPackStatusTextMap: Record<string, string> = {
  [SapPackStatus.NOT_PACKED]: '未发起打包开票',
  [SapPackStatus.PACKED]: '已发起打包开票',
  [SapPackStatus.INVOICED]: '已开票',
  [SapPackStatus.CANCELLED]: '已取消'
};

/** SAP 打包开票状态 → Tag 颜色 */
export const sapPackStatusColorMap: Record<string, string> = {
  [SapPackStatus.NOT_PACKED]: 'default',
  [SapPackStatus.PACKED]: 'processing',
  [SapPackStatus.INVOICED]: 'success',
  [SapPackStatus.CANCELLED]: 'default'
};

/** 80 单出库状态 → 显示文本，仅「已出库」允许发起独立开票 */
export const outStockStatusTextMap: Record<string, string> = {
  [OutStockStatus.NO_OUT]: '未出库',
  [OutStockStatus.PART_OUT]: '部分出库',
  [OutStockStatus.OUTED]: '已出库'
};

/** 独立开票列表「开票状态」多选项（与开票单状态同源） */
export const INDEPENDENT_BILLING_STATUS_OPTIONS: Array<{ label: string; value: BillingBillStatus }> = [
  { label: billingBillStatusTextMap[BillingBillStatus.PENDING], value: BillingBillStatus.PENDING },
  { label: billingBillStatusTextMap[BillingBillStatus.CONFIRMING], value: BillingBillStatus.CONFIRMING },
  { label: billingBillStatusTextMap[BillingBillStatus.CONFIRMED], value: BillingBillStatus.CONFIRMED }
];

/** 来源单据类型 → 显示文本 */
export const sourceDocTypeTextMap: Record<string, string> = {
  [SourceDocType.INTERNAL_TMS]: '内部发货单',
  [SourceDocType.EXTERNAL_ORDER]: '外部订单',
  [SourceDocType.PROPS_MATERIAL]: '道具物料订单',
  [SourceDocType.FREIGHT_VOUCHER]: '运费凭证',
  [SourceDocType.DIFF_ADJUST_INC]: '差异调整单(调增)',
  [SourceDocType.AB_DIFF_ADJUST]: 'AB差异调整',
  [SourceDocType.AFTER_SALE]: '售后单',
  [SourceDocType.EXTERNAL_AFTER_SALE]: '外部售后单',
  [SourceDocType.DIFF_ADJUST_DEC]: '差异调整单(调减)'
};
